import { computed } from 'vue'
import { DEFAULT_PROPS, VALID_BUTTON_TYPES } from '../constants/button-constants.js'

/**
 * Core button logic composable
 * @param {Object} props - Component props
 * @returns {Object} Button state and computed properties
 */
export function useButton(props) {
  /**
   * Check if button is disabled (disabled prop or processing)
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isDisabled = computed(() => {
    return props.disabled || props.processing
  })

  /**
   * Check if button is in processing state
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isProcessing = computed(() => {
    return props.processing
  })

  /**
   * Check if button has text content
   * @type {import('vue').ComputedRef<boolean>}
   */
  const hasText = computed(() => {
    return Boolean(props.text)
  })

  /**
   * Check if button has left icon
   * @type {import('vue').ComputedRef<boolean>}
   */
  const hasLeftIcon = computed(() => {
    return Boolean(props.leftIcon)
  })

  /**
   * Check if button has right icon
   * @type {import('vue').ComputedRef<boolean>}
   */
  const hasRightIcon = computed(() => {
    return Boolean(props.rightIcon)
  })

  /**
   * Check if button is icon-only (no text)
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isIconOnly = computed(() => {
    return !hasText.value && (hasLeftIcon.value || hasRightIcon.value)
  })

  /**
   * Get icon size class based on isLagerIcon prop
   * @type {import('vue').ComputedRef<string>}
   */
  const iconSizeClass = computed(() => {
    return props.isLagerIcon ? 'icon24' : 'icon16'
  })

  /**
   * Validate button type
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isValidType = computed(() => {
    return VALID_BUTTON_TYPES.includes(props.type)
  })

  /**
   * Get effective button type (fallback to primary if invalid)
   * @type {import('vue').ComputedRef<string>}
   */
  const effectiveType = computed(() => {
    return props.type;
    //return isValidType.value ? props.type : DEFAULT_PROPS.type
  })

  /**
   * Get effective border state (check both camelCase and kebab-case props)
   * @type {import('vue').ComputedRef<boolean>}
   */
  const effectiveHasBorder = computed(() => {
    return props.hasBorder || props['has-border'] || false
  })

  /**
   * Get effective shortkey target (check both camelCase and kebab-case props)
   * @type {import('vue').ComputedRef<string|null>}
   */
  const effectiveShortkeyTarget = computed(() => {
    return props.shortkeyTarget || props.shortkeyTarget || null
  })

  return {
    // State
    isDisabled,
    isProcessing,
    
    // Content checks
    hasText,
    hasLeftIcon,
    hasRightIcon,
    isIconOnly,
    
    // Styling
    iconSizeClass,
    effectiveType,
    isValidType,
    effectiveHasBorder,
    effectiveShortkeyTarget
  }
}
